import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, ServerCog } from "lucide-react";

const STATUS_ICON = {
    ok: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />,
    partial: <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />,
    failed: <XCircle className="w-3.5 h-3.5 text-red-600" />,
    skipped: <span className="text-[10px] text-muted-foreground font-mono">skip</span>,
};

const OVERALL_TONE = {
    ok: "border-emerald-500/50 bg-emerald-500/5",
    partial: "border-amber-500/50 bg-amber-500/5",
    failed: "border-red-500/50 bg-red-500/5",
};

function fmtDuration(ms) {
    if (ms == null) return "—";
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
}

function fmtTs(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString();
}

/**
 * Deployment Audit — the "what did that deploy actually do?" panel.
 * Reads from `deployment_events` populated by `server._background_warmup`.
 */
export default function DeploymentEventsCard() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/deployment-events?limit=30");
            setRows(data.rows || []);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const latest = rows[0];
    const toggle = (idx) => setExpanded((e) => ({ ...e, [idx]: !e[idx] }));

    return (
        <div className="space-y-4" data-testid="deployment-events-card">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-2xl font-extrabold tracking-tight flex items-center gap-2">
                        <ServerCog className="w-5 h-5 text-primary" /> Deployment Audit
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                        Every pod boot writes a row here with per-step status, duration, and errors. No pod-log grepping required.
                    </p>
                </div>
                <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="deployment-refresh-btn">
                    <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
                </Button>
            </div>

            {/* Latest deploy summary */}
            {latest && (
                <div className={`border rounded p-4 ${OVERALL_TONE[latest.overall_status] || "border-border bg-card"}`}
                     data-testid="deployment-latest">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                            {STATUS_ICON[latest.overall_status]}
                            <span className="overline text-xs text-muted-foreground">Latest boot</span>
                            <span className="font-mono text-xs">{latest.pod_name}</span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span>Duration <span className="mono font-semibold text-foreground">{fmtDuration(latest.duration_ms)}</span></span>
                            <span>Seed <span className="mono font-semibold text-foreground">{latest.seed_gated_off ? "off" : "on"}</span></span>
                            <span>{fmtTs(latest.started_at)}</span>
                        </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                        {Object.entries(latest.steps || {}).map(([name, s]) => (
                            <div key={name} className="border border-border rounded px-2 py-1.5 flex items-center gap-2 bg-background/40">
                                {STATUS_ICON[s.status] ?? <span className="text-muted-foreground">?</span>}
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold truncate">{name}</div>
                                    {s.built != null && (
                                        <div className="text-muted-foreground">
                                            {s.built} built{s.failed ? `, ${s.failed} failed` : ""}
                                        </div>
                                    )}
                                    {s.error && (
                                        <div className="text-red-600 truncate" title={s.error}>{s.error}</div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* History table */}
            <div className="border border-border bg-card rounded p-4" data-testid="deployment-history">
                <div className="overline text-xs text-muted-foreground mb-3">Last {rows.length} boots</div>
                {rows.length === 0 ? (
                    <div className="text-sm text-muted-foreground">No deploy events recorded yet.</div>
                ) : (
                    <div className="text-sm">
                        <div className="grid grid-cols-[100px_1fr_140px_140px_100px] gap-3 pb-2 border-b border-border/50 text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                            <div>Status</div>
                            <div>Pod</div>
                            <div>Started</div>
                            <div>Duration</div>
                            <div>Details</div>
                        </div>
                        {rows.map((r, i) => (
                            <div key={r.started_at || i} data-testid={`deployment-row-${i}`}>
                                <div className="grid grid-cols-[100px_1fr_140px_140px_100px] gap-3 py-2 border-b border-border/30 items-center">
                                    <div className="flex items-center gap-2">
                                        {STATUS_ICON[r.overall_status]}
                                        <span className="text-xs font-mono">{r.overall_status}</span>
                                    </div>
                                    <div className="font-mono text-xs truncate" title={r.pod_name}>{r.pod_name}</div>
                                    <div className="text-xs">{fmtTs(r.started_at)}</div>
                                    <div className="mono text-xs">{fmtDuration(r.duration_ms)}</div>
                                    <div>
                                        <button
                                            className="text-xs text-primary hover:underline"
                                            onClick={() => toggle(i)}
                                            data-testid={`deployment-toggle-${i}`}
                                        >
                                            {expanded[i] ? "Hide" : "Show"}
                                        </button>
                                    </div>
                                </div>
                                {expanded[i] && (
                                    <div className="pl-4 py-2 space-y-1 bg-muted/20 text-xs" data-testid={`deployment-details-${i}`}>
                                        {Object.entries(r.steps || {}).map(([name, s]) => (
                                            <div key={name} className="flex items-start gap-2">
                                                {STATUS_ICON[s.status]}
                                                <span className="font-semibold w-32 flex-shrink-0">{name}</span>
                                                <span className="text-muted-foreground">{s.status}</span>
                                                {s.built != null && (
                                                    <span className="text-muted-foreground">
                                                        · {s.built} built{s.failed ? `, ${s.failed} failed` : ""}
                                                    </span>
                                                )}
                                                {s.error && <span className="text-red-600 flex-1 break-all">{s.error}</span>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
